import { authService } from './authService';
import { getServerURL } from './config';

const SERVER_URL = getServerURL();

export const printingsService = {
  // Generate printings (creates async job)
  async generatePrintings(date = null, printingProfile = 'standard') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const body = { printingProfile };
      if (date) body.date = date;

      const response = await fetch(`${SERVER_URL}/printings/generate`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error generating printings:', error);
      throw error;
    }
  },

  // Get printing job status by ID
  async getPrintingById(id) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/printings/${id}`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching printing:', error);
      throw error;
    }
  },

  // Get all printing jobs (paginated)
  async getAllPrintings(page = 1, limit = 20, search = '', printingProfile = 'standard') {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('printingProfile', printingProfile);
      if (search) {
        params.set('search', search);
      }

      const url = `${SERVER_URL}/printings?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching printings:', error);
      throw error;
    }
  },

  // Get all approved express generations
  async getExpressGenerations() {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/printings/express/pending`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching express generations:', error);
      throw error;
    }
  },

  // Bosta Integration Methods

  // Fetch tracking numbers from Bosta for a printing job
  async fetchBostaTracking(printingId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/printings/${printingId}/bosta/fetch-tracking`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching Bosta tracking:', error);
      throw error;
    }
  },

  // Fetch shipping slip from Bosta for a printing job
  async fetchBostaShippingSlip(printingId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const body = {
        requestedAwbType: options.requestedAwbType || 'A4',
        lang: options.lang || 'ar',
      };

      const response = await fetch(`${SERVER_URL}/printings/${printingId}/bosta/fetch-shipping-slip`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching Bosta shipping slip:', error);
      throw error;
    }
  },

  // Complete Bosta workflow (fetch tracking + shipping slip in one call)
  async fetchBostaAll(printingId, options = {}) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const body = {
        requestedAwbType: options.requestedAwbType || 'A4',
        lang: options.lang || 'ar',
      };

      const response = await fetch(`${SERVER_URL}/printings/${printingId}/bosta/fetch-all`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching Bosta data:', error);
      throw error;
    }
  },

  // Get stored Bosta shipping slip info
  async getBostaShippingSlip(printingId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/printings/${printingId}/bosta/shipping-slip`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting Bosta shipping slip:', error);
      throw error;
    }
  },

  // =============================================================================
  // PRINT VERIFICATION METHODS
  // =============================================================================

  // Get generations for a printing job with verification status
  async getGenerationsForPrinting(
    printingId,
    page = 1,
    limit = 50,
    statusFilter = null,
    printingProfile = 'standard',
    lane = null
  ) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('printingProfile', printingProfile);
      if (lane) params.set('lane', lane);
      if (statusFilter) {
        params.set('status', statusFilter);
      }

      const response = await fetch(`${SERVER_URL}/printings/${printingId}/generations?${params.toString()}`, {
        method: 'GET',
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching generations for printing:', error);
      throw error;
    }
  },

  // Mark generation as printed by barcode scan
  async markPrintedByBarcode(
    barcodeId,
    printingId = null,
    printingProfile = 'standard',
    lane = null
  ) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const body = { barcodeId, printingProfile };
      if (printingId) {
        body.printingId = printingId;
      }
      if (lane) body.lane = lane;

      const response = await fetch(`${SERVER_URL}/generations/mark-printed-by-barcode`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error marking generation as printed:', error);
      throw error;
    }
  },

  async prepareExpressPrinting(generationIds) {
    if (!SERVER_URL) {
      throw new Error('API server URL is not configured');
    }
    const response = await fetch(`${SERVER_URL}/printings/express/prepare`, {
      method: 'POST',
      headers: {
        ...authService.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ generationIds }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || `Request failed with status ${response.status}`);
    }
    return result;
  },

  // Bulk mark generations as printed (uses existing bulk status endpoint)
  async bulkMarkPrinted(ids, printingId = null, lane = null) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/generations/bulk/status`, {
        method: 'PATCH',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids,
          status: 'printed',
          ...(printingId ? { printingId } : {}),
          ...(lane ? { lane } : {}),
        }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error bulk marking as printed:', error);
      throw error;
    }
  },

  // Mark single generation as printed by generation_id
  async markGenerationPrinted(generationId) {
    if (!SERVER_URL) {
      console.error('SERVER_URL is not configured');
      throw new Error('API server URL is not configured');
    }

    try {
      const response = await fetch(`${SERVER_URL}/generations/${generationId}/printed`, {
        method: 'PATCH',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Response is not JSON
        }
        throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error marking generation as printed:', error);
      throw error;
    }
  },
};
